import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAeoQuickAnswers,
  buildLandingPathForMarket,
  getLandingMarketFromPath,
  getMarketInfo,
  getPagePathByKey,
  getSeoData,
  LandingPageKey,
} from './regional';
import type { LandingLocale } from './copy';

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let node = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!node) {
    node = document.createElement('meta');
    document.head.appendChild(node);
  }
  Object.entries(attrs).forEach(([key, value]) => node!.setAttribute(key, value));
}

function upsertLink(selector: string, attrs: Record<string, string>) {
  let node = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!node) {
    node = document.createElement('link');
    document.head.appendChild(node);
  }
  Object.entries(attrs).forEach(([key, value]) => node!.setAttribute(key, value));
}

function toOgLocale(htmlLang: string) {
  if (htmlLang === 'en') return 'en_US';
  if (htmlLang === 'es') return 'es_419';
  return htmlLang.replace('-', '_');
}

function getGeoMeta(market: ReturnType<typeof getLandingMarketFromPath>) {
  switch (market) {
    case 'es':
      return { region: '419', place: 'America Latina' };
    default:
      return { region: 'WORLD', place: 'Global' };
  }
}

function getAreaServedSchema(market: ReturnType<typeof getLandingMarketFromPath>, areaServedName: string) {
  if (market === 'es') {
    return ['Latin America'].map((region) => ({
      '@type': 'Country',
      name: region,
    }));
  }
  return areaServedName;
}

export default function LandingSeo({ pageKey, htmlLang, locale }: { pageKey: LandingPageKey; htmlLang: string; locale: LandingLocale }) {
  const location = useLocation();

  useEffect(() => {
    const market = getLandingMarketFromPath(location.pathname);
    const marketInfo = getMarketInfo(market);
    const seo = getSeoData(pageKey, market, locale);
    const pagePath = getPagePathByKey(pageKey);
    const canonicalPath = buildLandingPathForMarket(pagePath, market);
    const quickAnswers = getAeoQuickAnswers(pageKey, market, locale);
    const geo = getGeoMeta(market);
    const origin = window.location.origin;
    const englishUrl = `${origin}${pagePath}`;
    const spanishLatamUrl = `${origin}${buildLandingPathForMarket(pagePath, 'es')}`;
    const canonicalUrl = market === 'default'
      ? locale === 'es'
          ? spanishLatamUrl
          : englishUrl
      : `${origin}${canonicalPath}`;
    const ogImageUrl = `${origin}/elen11.png`;
    const availableLanguages = market === 'default' ? ['en', 'es'] : Array.from(new Set([htmlLang, 'es']));
    const pathByMarket = {
      en: englishUrl,
      es: spanishLatamUrl,
      'es-419': spanishLatamUrl,
      'x-default': englishUrl,
    };

    document.title = seo.title;
    document.documentElement.setAttribute('lang', htmlLang);

    upsertMeta('meta[name="description"]', { name: 'description', content: seo.description });
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: seo.keywords });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1' });
    upsertMeta('meta[name="geo.region"]', { name: 'geo.region', content: geo.region });
    upsertMeta('meta[name="geo.placename"]', { name: 'geo.placename', content: geo.place });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Elen1' });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: toOgLocale(htmlLang) });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImageUrl });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImageUrl });
    upsertMeta('meta[data-og-locale="es_419"]', { 'data-og-locale': 'es_419', property: 'og:locale:alternate', content: 'es_419' });

    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });
    Object.entries(pathByMarket).forEach(([hreflang, href]) => {
      upsertLink(`link[rel="alternate"][hreflang="${hreflang}"]`, { rel: 'alternate', hreflang, href });
    });

    const ldJson = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${origin}#organization`,
          name: 'Elen1',
          url: origin,
          logo: `${origin}/logo-elen1.png`,
          areaServed: marketInfo.areaServedName,
        },
        {
          '@type': 'WebSite',
          '@id': `${origin}#website`,
          name: 'Elen1',
          url: origin,
          inLanguage: ['en', 'es'],
        },
        {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          name: seo.title,
          description: seo.description,
          inLanguage: htmlLang,
          url: canonicalUrl,
          isPartOf: { '@id': `${origin}#website` },
        },
        {
          '@type': 'Service',
          '@id': `${canonicalUrl}#service`,
          name: seo.title,
          description: seo.description,
          serviceType: market === 'default' ? 'Healthcare AI EMR platform' : 'Plataforma EMR con IA',
          provider: { '@id': `${origin}#organization` },
          areaServed: getAreaServedSchema(market, marketInfo.areaServedName),
          availableLanguage: availableLanguages,
        },
        {
          '@type': 'FAQPage',
          '@id': `${canonicalUrl}#faq`,
          mainEntity: seo.faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        },
        {
          '@type': 'ItemList',
          '@id': `${canonicalUrl}#quick-answers`,
          name: market === 'default' ? 'Quick Answers' : 'Respuestas Rápidas',
          itemListElement: quickAnswers.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.question,
            description: item.answer,
          })),
        },
      ],
    };

    let script = document.getElementById('landing-seo-jsonld') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'landing-seo-jsonld';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.text = JSON.stringify(ldJson);
  }, [htmlLang, locale, location.pathname, pageKey]);

  return null;
}
